import type { ReactElement, ReactNode } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { SegmentedControl } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import {
    useScoringBoardTrackingService
} from "app/pages/sales-intelligence/modules/lead-scoring-agent/hooks/useScoringBoardTrackingService";
import type {
    ScoringScopeSegment
} from "app/pages/sales-intelligence/modules/lead-scoring-agent/utils/scoringRulesView";

/** The "applicable for me" segment only exists for admins, so its key is optional. */
export type ScoringBoardSegmentTranslationKeys = Record<Exclude<ScoringScopeSegment, "applicableForMe">, string> & {
    applicableForMe?: string;
};

type ScoringBoardHeaderProps = {
    segments: readonly ScoringScopeSegment[];
    segmentTranslationKeys: ScoringBoardSegmentTranslationKeys;
    /** Segments the board has no rules for — rendered, but not selectable. */
    segmentDisabled: Partial<Record<ScoringScopeSegment, boolean>>;
    selectedIndex: number;
    onSegmentChange: (index: number) => void;
    /** Board-level actions, aligned to the opposite edge of the segments. */
    actions?: ReactNode;
};

export const ScoringBoardHeader = ({
    segments,
    segmentTranslationKeys,
    segmentDisabled,
    selectedIndex,
    onSegmentChange,
    actions,
}: ScoringBoardHeaderProps): ReactElement => {
    const { translate } = useTranslate();
    const scoringBoardTracking = useScoringBoardTrackingService();

    const segmentValues = segments.map((segment) => ({
        value: segment,
        label: translate(segmentTranslationKeys[segment] ?? segment),
        disabled: segmentDisabled[segment] ?? false,
    }));

    const handleSegmentChange = (index: number) => {
        scoringBoardTracking.trackChangeRulesSegmentFilter(segments[index]);
        onSegmentChange(index);
    };

    return (
        <Flex justifyContent="space-between" alignItems="center" gap="16px">
            <SegmentedControl values={segmentValues} selectedIndex={selectedIndex} onChange={handleSegmentChange} />

            {actions}
        </Flex>
    );
};
